/**
 * AI Agent Context
 * Manages state for AI agent interactions
 *
 * NOTE: The useAIAgent hook is exported from this file for backward compatibility,
 * but is also available from './useAIAgent' for better Fast Refresh support.
 */
import React, { createContext, useState, useCallback } from 'react';
import { useAuth } from './useAuth';
import { ServiceFactory } from '../services/factory/ServiceFactory';
import { ActionExecutor } from '../services/ai-agent/ActionExecutor';
import { useNavigate } from 'react-router-dom';

interface ActionHistory {
  id: string;
  timestamp: Date;
  actionPlan: any;
  result: any;
  undoable: boolean;
  status?: 'completed' | 'undone' | 'failed';
  description?: string;
}

export interface AIAgentContextType {
  // State
  isVisible: boolean;
  isProcessing: boolean;
  actionHistory: ActionHistory[];
  currentContext: any;
  
  // Actions
  showAgent: () => void;
  hideAgent: () => void;
  toggleAgent: () => void;
  processRequest: (request: string) => Promise<any>;
  executeAction: (actionPlan: any) => Promise<any>;
  undoLastAction: () => Promise<any>;
  undoAction: (actionId: string) => Promise<any>;
  updateContext: (context: any) => void;
}

export const AIAgentContext = createContext<AIAgentContextType | null>(null);

export const AIAgentProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isVisible, setIsVisible] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [actionHistory, setActionHistory] = useState<ActionHistory[]>([]);
  const [currentContext, setCurrentContext] = useState<any>({
    pageType: 'dashboard',
    pageData: {}
  });
  
  const { user } = useAuth();
  const navigate = useNavigate();
  
  // Initialize services
  const aiAgentService = ServiceFactory.getAIAgentService();
  const dashboardService = ServiceFactory.getDashboardService();
  const exportService = ServiceFactory.getExportService();
  
  const actionExecutor = new ActionExecutor(
    navigate,
    dashboardService,
    exportService
  );

  const showAgent = useCallback(() => {
    setIsVisible(true);
  }, []);

  const hideAgent = useCallback(() => {
    setIsVisible(false);
  }, []);

  const toggleAgent = useCallback(() => {
    setIsVisible(prev => !prev);
  }, []);

  const processRequest = useCallback(async (request: string | { message: string; conversationId?: string; conversationState?: any }) => {
    if (!user?.churchId) {
      return {
        success: false,
        message: 'No church context available',
      };
    }

    try {
      setIsProcessing(true);

      // Handle both string and object request formats
      let requestText: string;
      let conversationId: string | undefined;
      let conversationState: any;

      if (typeof request === 'string') {
        requestText = request;
      } else {
        requestText = request.message;
        conversationId = request.conversationId;
        conversationState = request.conversationState;
      }

      // Get current page context
      const context = {
        ...currentContext,
        currentPath: window.location.pathname,
        timestamp: new Date().toISOString(),
        ...(conversationState && { conversationState }),
      };

      // Process through backend
      const result = await aiAgentService.processRequest({
        request: requestText,
        churchId: user.churchId,
        userId: user.id,
        context,
        ...(conversationId && { conversation_id: conversationId }),
      } as any);

      return result;
    } catch (error) {
      console.error('Process request error:', error);
      return {
        success: false,
        message: 'Failed to process request',
        error,
      };
    } finally {
      setIsProcessing(false);
    }
  }, [user, currentContext]);

  const executeAction = useCallback(async (actionPlan: any) => {
    if (!user) {
      return {
        success: false,
        error: 'Not authenticated',
      };
    }

    try {
      setIsProcessing(true);

      // Handle case where there's no action_plan (informational responses)
      if (!actionPlan?.action_plan?.actions || actionPlan.action_plan.actions.length === 0) {
        return {
          success: true,
          results: [],
          errors: [],
        };
      }

      // Check permissions
      const permissionCheck = actionExecutor.canExecute(
        actionPlan.action_plan,
        user.permissions || []
      );

      if (!permissionCheck.canExecute) {
        return {
          success: false,
          error: `Missing permissions: ${permissionCheck.missingPermissions.join(', ')}`,
        };
      }

      // Execute actions
      const result = await actionExecutor.executeActionPlan(
        actionPlan.action_plan
      );
      
      // Add to history if successful
      if (result.success) {
        const historyEntry: ActionHistory = {
          id: Date.now().toString(),
          timestamp: new Date(),
          actionPlan: actionPlan,
          result: result,
          undoable: result.results.some((r: any) => r.undoable),
          status: 'completed',
          description: actionPlan.action_plan?.description || 'AI Action',
        };
        
        setActionHistory(prev => [...prev, historyEntry]);
      }
      
      return result;
    } catch (error) {
      console.error('Execute action error:', error);
      return {
        success: false,
        error: 'Failed to execute action',
      };
    } finally {
      setIsProcessing(false);
    }
  }, [user, actionExecutor]);

  const undoLastAction = useCallback(async () => {
    const lastAction = actionHistory[actionHistory.length - 1];
    if (!lastAction || !lastAction.undoable) {
      return {
        success: false,
        message: 'No undoable actions',
      };
    }

    try {
      setIsProcessing(true);
      
      // Generate undo actions
      const undoActions = actionExecutor.generateUndoActions(
        lastAction.actionPlan.action_plan,
        lastAction.result.results
      );
      
      if (undoActions.length === 0) {
        return {
          success: false,
          message: 'Cannot undo this action',
        };
      }
      
      // Execute undo
      const undoResult = await actionExecutor.executeActionPlan({
        actions: undoActions,
        needs_confirmation: false,
        intent: { action_type: 'undo' },
      });
      
      if (undoResult.success) {
        // Update status in history
        setActionHistory(prev => 
          prev.map(action => 
            action.id === lastAction.id 
              ? { ...action, status: 'undone' }
              : action
          )
        );
      }
      
      return undoResult;
    } catch (error) {
      console.error('Undo error:', error);
      return {
        success: false,
        message: 'Failed to undo action',
      };
    } finally {
      setIsProcessing(false);
    }
  }, [actionHistory, actionExecutor]);

  const undoAction = useCallback(async (actionId: string) => {
    const action = actionHistory.find(a => a.id === actionId);
    if (!action || !action.undoable || action.status !== 'completed') {
      return {
        success: false,
        error: 'Action cannot be undone',
      };
    }

    try {
      setIsProcessing(true);
      
      // Call backend undo service
      const response = await fetch(`/api/v1/ai/agent/undo/${actionId}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`,
        },
      });

      const result = await response.json();
      
      if (result.success) {
        // Update status in history
        setActionHistory(prev => 
          prev.map(a => 
            a.id === actionId 
              ? { ...a, status: 'undone' }
              : a
          )
        );
      }
      
      return result;
    } catch (error) {
      console.error('Undo error:', error);
      return {
        success: false,
        error: 'Failed to undo action',
      };
    } finally {
      setIsProcessing(false);
    }
  }, [actionHistory]);

  const updateContext = useCallback((context: any) => {
    setCurrentContext(prev => ({
      ...prev,
      ...context,
    }));
  }, []);

  const value: AIAgentContextType = {
    isVisible,
    isProcessing,
    actionHistory,
    currentContext,
    showAgent,
    hideAgent,
    toggleAgent,
    processRequest,
    executeAction,
    undoLastAction,
    undoAction,
    updateContext,
  };

  return (
    <AIAgentContext.Provider value={value}>
      {children}
    </AIAgentContext.Provider>
  );
};

// Re-export useAIAgent from separate file for Fast Refresh compatibility
// This maintains backward compatibility for existing imports
export { useAIAgent } from './useAIAgent';
